import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { TwilioReadinessCard } from "@/components/TwilioReadinessCard";
import { Clock, Save, CalendarDays, CheckCircle2, AlertTriangle, Unlink, ExternalLink, Mail, ShieldCheck, PhoneForwarded } from "lucide-react";

const DAYS = [
  { v: 0, label: "Mon" }, { v: 1, label: "Tue" }, { v: 2, label: "Wed" },
  { v: 3, label: "Thu" }, { v: 4, label: "Fri" }, { v: 5, label: "Sat" }, { v: 6, label: "Sun" },
];
const TZ_OPTIONS = [
  "America/Los_Angeles", "America/Denver", "America/Phoenix", "America/Chicago",
  "America/New_York", "America/Anchorage", "Pacific/Honolulu", "UTC",
];

const A2P_FIELDS = [
  { k: "twilio_messaging_service_sid", label: "Messaging Service SID", ph: "MG…" },
  { k: "a2p_brand_sid", label: "A2P Brand SID", ph: "BN…" },
  { k: "a2p_campaign_sid", label: "A2P Campaign SID", ph: "CM…" },
];

function A2PCard({ initial, canEdit }) {
  const [vals, setVals] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => { setVals(initial || {}); }, [initial]);
  const save = async () => {
    setBusy(true);
    try {
      const body = Object.fromEntries(A2P_FIELDS.map(f => [f.k, (vals[f.k] || "").trim()]));
      const r = await api.patch("/org/settings", body);
      setVals(r.data.a2p || {});
      toast.success("A2P registration saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    setBusy(false);
  };
  return (
    <Card className="bg-slate-900 border-slate-800 p-6 mt-6" data-testid="a2p-card">
      <h2 className="font-display text-xl font-bold flex items-center gap-2">
        <ShieldCheck className="w-5 h-5 text-orange-400" /> SMS registration (A2P 10DLC)
      </h2>
      <p className="text-slate-400 text-sm mt-1 mb-4">
        Your company's own Twilio Brand and Campaign. Texts switch to your business name only after the Texting readiness check below passes.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {A2P_FIELDS.map(f => (
          <div key={f.k}>
            <Label className="text-sm mb-2 block">{f.label}</Label>
            <Input data-testid={`settings-${f.k}`} value={vals[f.k] || ""} placeholder={f.ph} disabled={!canEdit}
              onChange={(e) => setVals({ ...vals, [f.k]: e.target.value })}
              className="bg-slate-800 border-slate-700 font-mono text-xs" />
          </div>
        ))}
      </div>
      {canEdit && (
        <Button onClick={save} disabled={busy} data-testid="settings-save-a2p-btn"
          className="mt-4 bg-orange-500 hover:bg-orange-600 text-slate-900 font-semibold">
          <Save className="w-4 h-4 mr-2" /> {busy ? "Saving…" : "Save registration"}
        </Button>
      )}
    </Card>
  );
}

export default function Settings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sched, setSched] = useState(null);
  const [org, setOrg] = useState(null);
  const [notifyEmail, setNotifyEmail] = useState("");
  const [notifyIsCustom, setNotifyIsCustom] = useState(false);
  const [savingNotify, setSavingNotify] = useState(false);
  const [forwardTo, setForwardTo] = useState("");
  const [savingForward, setSavingForward] = useState(false);

  const saveForwardTo = async () => {
    setSavingForward(true);
    try {
      const r = await api.patch("/org/settings", { forward_to_number: forwardTo.trim() });
      setForwardTo(r.data.forward_to_number || "");
      toast.success(r.data.forward_to_number ? "Call forwarding saved" : "Call forwarding turned off");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save forwarding number");
    }
    setSavingForward(false);
  };

  // Google Calendar state
  const [gcal, setGcal] = useState(null); // {platform_configured, connected, google_email, calendar_id, calendar_summary, needs_calendar_selection}
  const [cals, setCals] = useState([]);
  const [picking, setPicking] = useState("");
  const [gcalBusy, setGcalBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [s, g] = await Promise.all([
        api.get("/org/settings"),
        api.get("/google-calendar/status"),
      ]);
      setOrg(s.data);
      setForwardTo(s.data.forward_to_number || "");
      setSched(s.data.scheduling);
      setNotifyEmail(s.data.notification_email || "");
      setNotifyIsCustom(!!s.data.notification_email_is_custom);
      setGcal(g.data);
      setPicking(g.data.calendar_id || "");
    } catch { toast.error("Could not load settings"); }
    setLoading(false);
  };
  useEffect(() => {
    load();
    // Pick up ?gcal=connected|error after returning from Google
    const sp = new URLSearchParams(window.location.search);
    const gc = sp.get("gcal");
    if (gc === "connected") toast.success("Google Calendar connected");
    else if (gc === "error") toast.error(`Google Calendar connection failed (${sp.get("reason") || "unknown"})`);
    if (gc) {
      sp.delete("gcal"); sp.delete("reason");
      const q = sp.toString();
      window.history.replaceState({}, "", window.location.pathname + (q ? "?" + q : ""));
    }
  }, []);

  const loadCalendars = async () => {
    try {
      const r = await api.get("/google-calendar/calendars");
      setCals(r.data.calendars || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not list calendars");
    }
  };
  useEffect(() => { if (gcal?.connected) loadCalendars(); /* eslint-disable-next-line */ }, [gcal?.connected]);

  const toggleDay = (v) => {
    const cur = new Set(sched.working_days);
    cur.has(v) ? cur.delete(v) : cur.add(v);
    setSched({ ...sched, working_days: Array.from(cur).sort((a,b)=>a-b) });
  };

  const saveNotifyEmail = async () => {
    setSavingNotify(true);
    try {
      const r = await api.patch("/org/settings", { notification_email: notifyEmail.trim() });
      setNotifyEmail(r.data.notification_email || "");
      // Refresh the custom flag from the server
      const s = await api.get("/org/settings");
      setNotifyEmail(s.data.notification_email || "");
      setNotifyIsCustom(!!s.data.notification_email_is_custom);
      toast.success(notifyEmail.trim() ? "Notification email updated" : "Using owner email for alerts");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
    setSavingNotify(false);
  };

  const save = async () => {
    if (!sched.working_days.length) return toast.error("Pick at least one working day");
    setSaving(true);
    try {
      const r = await api.patch("/org/settings", {
        working_days: sched.working_days,
        working_hours: sched.working_hours,
        estimate_duration_minutes: Number(sched.estimate_duration_minutes),
        buffer_minutes: Number(sched.buffer_minutes),
        timezone: sched.timezone,
      });
      setSched(r.data.scheduling);
      toast.success("Scheduling settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
    setSaving(false);
  };

  const connectGcal = async () => {
    setGcalBusy(true);
    try {
      const r = await api.get("/oauth/google/calendar/authorize");
      window.location.href = r.data.authorization_url;
    } catch (e) {
      setGcalBusy(false);
      toast.error(e?.response?.data?.detail || "Could not start Google OAuth");
    }
  };

  const saveCalendarPick = async () => {
    if (!picking) return toast.error("Pick a calendar first");
    const chosen = cals.find(c => c.id === picking);
    setGcalBusy(true);
    try {
      await api.patch("/google-calendar/settings", {
        calendar_id: picking,
        calendar_summary: chosen?.summary,
      });
      toast.success(`Using "${chosen?.summary || picking}"`);
      const g = await api.get("/google-calendar/status");
      setGcal(g.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save selection");
    }
    setGcalBusy(false);
  };

  const disconnectGcal = async () => {
    if (!window.confirm("Disconnect Google Calendar?\n\nFuture bookings won't create Google events. Existing Google events are not deleted.")) return;
    setGcalBusy(true);
    try {
      await api.delete("/google-calendar");
      toast.success("Google Calendar disconnected");
      setCals([]);
      setPicking("");
      const g = await api.get("/google-calendar/status");
      setGcal(g.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Disconnect failed");
    }
    setGcalBusy(false);
  };

  if (loading || !sched) {
    return <div className="p-10 text-slate-400" data-testid="settings-loading">Loading settings…</div>;
  }

  return (
    <div className="p-6 lg:p-10 max-w-3xl mx-auto" data-testid="settings-page">
      <header className="mb-8">
        <p className="font-mono text-xs uppercase tracking-widest text-orange-400 mb-2">Settings</p>
        <h1 className="font-display text-3xl lg:text-4xl font-bold tracking-tight">
          {org?.name} · Scheduling
        </h1>
        <p className="text-slate-400 text-sm mt-2">
          Control when homeowners can book estimates with your team. Changes apply immediately.
        </p>
      </header>

      <Card className="bg-slate-900 border-slate-800 p-6 space-y-6">
        <div>
          <Label className="text-sm font-semibold flex items-center gap-2 mb-3">
            <Clock className="w-4 h-4 text-orange-400" /> Working days
          </Label>
          <div className="flex flex-wrap gap-2" data-testid="settings-working-days">
            {DAYS.map((d) => {
              const active = sched.working_days.includes(d.v);
              return (
                <button
                  key={d.v} type="button"
                  data-testid={`day-${d.label.toLowerCase()}`}
                  onClick={() => toggleDay(d.v)}
                  className={`px-4 py-2 rounded-md text-sm font-mono border transition-colors ${
                    active
                      ? "bg-orange-500/20 border-orange-500/50 text-orange-200"
                      : "bg-slate-800/50 border-slate-700 text-slate-400 hover:border-slate-600"
                  }`}
                >{d.label}</button>
              );
            })}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-sm mb-2 block">Working hours start</Label>
            <Input type="time" data-testid="settings-hours-start"
              value={sched.working_hours.start}
              onChange={(e) => setSched({ ...sched, working_hours: { ...sched.working_hours, start: e.target.value } })}
              className="bg-slate-800 border-slate-700" />
          </div>
          <div>
            <Label className="text-sm mb-2 block">Working hours end</Label>
            <Input type="time" data-testid="settings-hours-end"
              value={sched.working_hours.end}
              onChange={(e) => setSched({ ...sched, working_hours: { ...sched.working_hours, end: e.target.value } })}
              className="bg-slate-800 border-slate-700" />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-sm mb-2 block">Appointment duration (min)</Label>
            <Input type="number" min="5" max="480" data-testid="settings-duration"
              value={sched.estimate_duration_minutes}
              onChange={(e) => setSched({ ...sched, estimate_duration_minutes: e.target.value })}
              className="bg-slate-800 border-slate-700" />
          </div>
          <div>
            <Label className="text-sm mb-2 block">Buffer between appts (min)</Label>
            <Input type="number" min="0" max="240" data-testid="settings-buffer"
              value={sched.buffer_minutes}
              onChange={(e) => setSched({ ...sched, buffer_minutes: e.target.value })}
              className="bg-slate-800 border-slate-700" />
          </div>
        </div>

        <div>
          <Label className="text-sm mb-2 block">Time zone</Label>
          <Select value={sched.timezone} onValueChange={(v) => setSched({ ...sched, timezone: v })}>
            <SelectTrigger data-testid="settings-timezone" className="bg-slate-800 border-slate-700">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
              {TZ_OPTIONS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div className="pt-2">
          <Button onClick={save} disabled={saving} data-testid="settings-save-btn"
            className="bg-orange-500 hover:bg-orange-600 text-slate-900 font-semibold">
            <Save className="w-4 h-4 mr-2" /> {saving ? "Saving…" : "Save scheduling settings"}
          </Button>
        </div>
      </Card>

      {/* ---- Notifications section ---- */}
      <Card className="bg-slate-900 border-slate-800 p-6 mt-6" data-testid="notifications-card">
        <div className="mb-4">
          <h2 className="font-display text-xl font-bold flex items-center gap-2">
            <Mail className="w-5 h-5 text-orange-400" /> Email alerts
          </h2>
          <p className="text-slate-400 text-sm mt-1">
            Where we send new-lead and booking notifications. Defaults to the owner's login email — point it anywhere you want (dispatch inbox, shared alias, your phone's email-to-SMS address).
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 items-end">
          <div>
            <Label className="text-sm mb-2 block">Notification email</Label>
            <Input
              type="email"
              data-testid="settings-notification-email"
              value={notifyEmail}
              onChange={(e) => setNotifyEmail(e.target.value)}
              placeholder="alerts@yourcompany.com"
              className="bg-slate-800 border-slate-700"
            />
            <p className="text-xs text-slate-500 mt-2">
              {notifyIsCustom
                ? "Custom address — leave blank to fall back to the owner's login email."
                : "Currently using the owner's login email. Set a custom one to override."}
            </p>
          </div>
          <Button
            onClick={saveNotifyEmail}
            disabled={savingNotify}
            data-testid="settings-save-notify-btn"
            className="bg-orange-500 hover:bg-orange-600 text-slate-900 font-semibold"
          >
            <Save className="w-4 h-4 mr-2" /> {savingNotify ? "Saving…" : "Save"}
          </Button>
        </div>
      </Card>

      {/* ---- Call forwarding section ---- */}
      <Card className="bg-slate-900 border-slate-800 p-6 mt-6" data-testid="forwarding-card">
        <div className="mb-4">
          <h2 className="font-display text-xl font-bold flex items-center gap-2">
            <PhoneForwarded className="w-5 h-5 text-orange-400" /> Call forwarding
          </h2>
          <p className="text-slate-400 text-sm mt-1">
            Calls to your RoofLead number ring this phone first. You'll hear "press 1 to accept" — if you don't answer or your voicemail picks up, the caller goes to the missed-call flow. Leave blank to send every call straight to the missed-call flow.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 items-end">
          <div>
            <Label className="text-sm mb-2 block">Forward calls to</Label>
            <Input
              type="tel"
              data-testid="settings-forward-to-number"
              value={forwardTo}
              onChange={(e) => setForwardTo(e.target.value)}
              placeholder="+15125550100"
              className="bg-slate-800 border-slate-700"
              disabled={!["owner", "admin"].includes(org?.role)}
            />
          </div>
          <Button
            onClick={saveForwardTo}
            disabled={savingForward || !["owner", "admin"].includes(org?.role)}
            data-testid="settings-save-forward-btn"
            className="bg-orange-500 hover:bg-orange-600 text-slate-900 font-semibold"
          >
            <Save className="w-4 h-4 mr-2" /> {savingForward ? "Saving…" : "Save"}
          </Button>
        </div>
      </Card>

      <A2PCard initial={org?.a2p} canEdit={["owner", "admin"].includes(org?.role)} />
      <TwilioReadinessCard canEdit={["owner", "admin"].includes(org?.role)} />

      {/* ---- Google Calendar section ---- */}
      <Card className="bg-slate-900 border-slate-800 p-6 mt-6" data-testid="gcal-card">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <h2 className="font-display text-xl font-bold flex items-center gap-2">
              <CalendarDays className="w-5 h-5 text-orange-400" /> Google Calendar
            </h2>
            <p className="text-slate-400 text-sm mt-1">
              When connected, RoofLead reads your Google Calendar for availability conflicts and writes every booked estimate as a real Google event.
            </p>
          </div>
        </div>

        {!gcal?.platform_configured && (
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-md p-4 text-sm text-amber-200 flex items-start gap-3" data-testid="gcal-platform-missing">
            <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold">Google OAuth is not configured on this environment yet.</p>
              <p className="text-amber-300/80 mt-1">
                Add <code className="font-mono">GOOGLE_OAUTH_CLIENT_ID</code> and <code className="font-mono">GOOGLE_OAUTH_CLIENT_SECRET</code> in Manage Publishing → Secrets, then redeploy.
              </p>
            </div>
          </div>
        )}

        {gcal?.platform_configured && gcal?.needs_reconnect && (
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-md p-4 text-sm text-amber-200 flex items-start gap-3" data-testid="gcal-needs-reconnect">
            <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
            <div className="space-y-3">
              <p className="font-semibold">Reconnect needed — Google stopped accepting RoofLead's saved login{gcal.google_email ? ` for ${gcal.google_email}` : ""}.</p>
              <p className="text-amber-300/80">New appointments won't reach Google Calendar until you reconnect.</p>
              <div className="flex gap-2">
                <Button onClick={connectGcal} disabled={gcalBusy} data-testid="gcal-reconnect-btn"
                  className="bg-white text-slate-900 hover:bg-slate-100 font-semibold">
                  <ExternalLink className="w-4 h-4 mr-2" /> {gcalBusy ? "Opening Google…" : "Reconnect Google Calendar"}
                </Button>
                <Button onClick={disconnectGcal} disabled={gcalBusy} variant="ghost" data-testid="gcal-reconnect-disconnect-btn"
                  className="text-slate-300 hover:bg-slate-800">Disconnect</Button>
              </div>
            </div>
          </div>
        )}

        {gcal?.platform_configured && !gcal?.connected && !gcal?.needs_reconnect && (
          <div data-testid="gcal-not-connected">
            <p className="text-slate-300 text-sm mb-4">
              Not connected yet. RoofLead will fall back to its own database for availability until you connect.
            </p>
            <Button onClick={connectGcal} disabled={gcalBusy} data-testid="gcal-connect-btn"
              className="bg-white text-slate-900 hover:bg-slate-100 font-semibold">
              <ExternalLink className="w-4 h-4 mr-2" />
              {gcalBusy ? "Opening Google…" : "Connect Google Calendar"}
            </Button>
          </div>
        )}

        {gcal?.connected && (
          <div className="space-y-4" data-testid="gcal-connected">
            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-md p-4 flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 shrink-0 mt-0.5 text-emerald-400" />
              <div className="text-sm">
                <p className="text-emerald-200 font-semibold">Connected as {gcal.google_email || "your Google account"}</p>
                {gcal.calendar_summary && (
                  <p className="text-emerald-300/80 mt-1">
                    Using calendar: <span className="font-mono">{gcal.calendar_summary}</span>
                  </p>
                )}
                {gcal.health?.reason === "google_unreachable" && (
                  <p className="text-amber-300 mt-1" data-testid="gcal-health-unreachable">Couldn't reach Google just now — status will re-check on next load.</p>
                )}
                {gcal.needs_calendar_selection && (
                  <p className="text-amber-300 mt-1">Pick which calendar RoofLead should use ↓</p>
                )}
              </div>
            </div>

            <div>
              <Label className="text-sm mb-2 block">Which calendar should RoofLead use for estimates?</Label>
              <Select value={picking} onValueChange={setPicking}>
                <SelectTrigger data-testid="gcal-calendar-picker" className="bg-slate-800 border-slate-700">
                  <SelectValue placeholder={cals.length ? "Pick a calendar…" : "Loading your calendars…"} />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
                  {cals.map(c => (
                    <SelectItem key={c.id} value={c.id} data-testid={`gcal-cal-opt-${c.id}`}>
                      {c.summary} {c.primary ? "(primary)" : ""} <span className="text-slate-500 text-xs">· {c.timeZone}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-3">
              <Button onClick={saveCalendarPick} disabled={gcalBusy || !picking || picking === gcal.calendar_id}
                data-testid="gcal-save-pick-btn"
                className="bg-orange-500 hover:bg-orange-600 text-slate-900 font-semibold">
                Use this calendar
              </Button>
              <Button onClick={disconnectGcal} disabled={gcalBusy}
                variant="ghost" data-testid="gcal-disconnect-btn"
                className="text-rose-400 hover:text-rose-300 hover:bg-rose-500/10">
                <Unlink className="w-4 h-4 mr-2" /> Disconnect
              </Button>
            </div>
          </div>
        )}
      </Card>

      <p className="text-xs text-slate-500 mt-6">
        Estimators are managed on the <a href="/app/team" className="text-orange-400 hover:text-orange-300 underline">Team &amp; Access</a> page — flip the <span className="text-slate-300">Bookable</span> switch to control who shows up in the Estimate Calendar.
      </p>

      <details className="mt-6 group" data-testid="settings-advanced">
        <summary className="text-xs text-slate-500 font-mono cursor-pointer hover:text-slate-400 select-none inline-flex items-center gap-1">
          <span className="group-open:rotate-90 transition-transform inline-block">▸</span> Advanced / developer info
        </summary>
        <div className="mt-2 pl-4 border-l border-slate-800 space-y-1">
          <p className="text-xs text-slate-500 font-mono">Tenant ID <span className="text-slate-400">{org?.id}</span></p>
          <p className="text-xs text-slate-500 font-mono">Twilio number <span className="text-slate-400">{org?.twilio_phone || "— not assigned —"}</span></p>
        </div>
      </details>
    </div>
  );
}
